import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "../../styles/pages/home/PublicEventDeck.module.scss";


export default function PublicEventDeck({
  events = [],
  autoplay = true,
  intervalMs = 3600,
  startIndex = 0,
  accent = "#31e6ff",
  eyebrow = "Appearances",
  title = "Conference and Events",
  subtitle = "Talks, panels, and hands-on work.",
  variant = "wide",
}) {
  const safeEvents = Array.isArray(events) ? events : [];
  const count = safeEvents.length;
  const [index, setIndex] = useState(Math.min(Math.max(startIndex, 0), Math.max(count - 1, 0)));
  const [prevIndex, setPrevIndex] = useState(null);
  const [direction, setDirection] = useState(1);
  const [hovering, setHovering] = useState(false);
  const progressRef = useRef(null);
  const touchStartX = useRef(null);
  const sectionId = useRef(`events-${Math.random().toString(36).slice(2, 8)}`).current;

  const current = count ? safeEvents[index] : null;

  const goTo = (nextIdx, dir = 1) => {
    if (!count) return;
    setPrevIndex(index);
    setIndex((nextIdx + count) % count);
    setDirection(dir);
  };

  const next = () => goTo(index + 1, 1);
  const prev = () => goTo(index - 1, -1);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (!autoplay || hovering || count < 2 || media.matches) return;
    const id = setInterval(next, intervalMs);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoplay, hovering, count, index, intervalMs]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, count]);

  const onTouchStart = (e) => (touchStartX.current = e.touches[0].clientX);
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 40) (dx < 0 ? next : prev)();
    touchStartX.current = null;
  };

  useEffect(() => {
    if (!progressRef.current) return;
    progressRef.current.style.animation = "none";
    // restart CSS animation
    // eslint-disable-next-line no-unused-expressions
    progressRef.current.offsetHeight;
    progressRef.current.style.animation = `${styles.progressFill} ${intervalMs}ms linear`;
  }, [index, intervalMs]);

  const announce = useMemo(() => {
    if (!current) return "";
    const bits = [current.title, current.role, formatDateRange(current.date, current.endDate), current.location]
      .filter(Boolean)
      .join(" • ");
    return bits;
  }, [current]);

  return (
    <section
      className={`${styles.deck} ${styles[variant] || ""}`}
      data-theme="dark"
      style={{ ["--accent"]: accent }}
      aria-roledescription="carousel"
      aria-labelledby={sectionId}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className={styles.bg} aria-hidden="true" />
      <div className={styles.grain} aria-hidden="true" />

      <Header eyebrow={eyebrow} title={title} subtitle={subtitle} sectionId={sectionId} />

      <div className={styles.srOnly} aria-live="polite">{announce}</div>

      <div className={styles.viewport}>
        {prevIndex != null && (
          <EventCard
            key={`prev-${prevIndex}`}
            data={safeEvents[prevIndex]}
            state="exit"
            direction={direction}
          />
        )}
        {count > 0 && (
          <EventCard key={`curr-${index}`} data={current} state="enter" direction={direction} />
        )}
      </div>

      <div className={styles.controls}>
        <button className={styles.navBtn} aria-label="Previous event" title="Previous" onClick={prev}>
          <ChevronLeft />
        </button>
        <div className={styles.counter}>
          {index + 1} <span className={styles.muted}>/ {count}</span>
        </div>
        <button className={styles.navBtn} aria-label="Next event" title="Next" onClick={next}>
          <ChevronRight />
        </button>
      </div>

      <div className={styles.dots} role="tablist" aria-label="Event slides">
        {safeEvents.map((ev, i) => (
          <button
            key={ev.id ?? i}
            role="tab"
            aria-selected={i === index}
            className={`${styles.dot} ${i === index ? styles.active : ""}`}
            onClick={() => goTo(i, i > index ? 1 : -1)}
            title={ev.title}
          />
        ))}
      </div>

      {autoplay && (
        <div className={styles.progress} aria-hidden="true">
          <div className={styles.progressBar} ref={progressRef} />
        </div>
      )}
    </section>
  );
}

function Header({ eyebrow, title, subtitle, sectionId }) {
  return (
    <header className={styles.header}>
      {eyebrow && <div className={styles.eyebrow}>{eyebrow}</div>}
      {title && <h2 id={sectionId} className={styles.h2}>{title}</h2>}
      {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
    </header>
  );
}

function EventCard({ data, state, direction }) {
  const {
    id,
    title,
    role,
    org,
    subtitle,
    date,
    endDate,
    location,
    description,
    image,
    link,
    tags = [],
    experience,
    stats = [],
    details,
  } = data || {};
  const [open, setOpen] = useState(false);

  const cl = [
    styles.card,
    state === "enter" ? styles.enter : styles.exit,
    direction === 1 ? styles.forward : styles.backward,
  ].join(" ");

  const panelId = `event-more-${id ?? title?.replace(/\s+/g, "-") ?? "x"}`;

  // build a default subtitle when not provided
  const builtSubtitle =
    subtitle ||
    [formatDateRange(date, endDate), location].filter(Boolean).join(" · ");

  return (
    <article className={cl} aria-roledescription="slide">
      {image && (
        <div className={styles.media}>
          <img src={image} alt="" loading="lazy" />
          <div className={styles.mediaGlow} aria-hidden="true" />
        </div>
      )}

      <div className={styles.body}>
        {/* Org + role kicker */}
        <div className={styles.kickerRow}>
          {role && <span className={styles.roleBadge}>{role}</span>}
          {org && <span className={styles.org}>{org}</span>}
        </div>



        <h3 className={styles.title}>{title}</h3>
        {(builtSubtitle || description) && (
          <p className={styles.subdesc}>{builtSubtitle}</p>
        )}

        {/* Tags */}
        {Array.isArray(tags) && tags.length > 0 && (
          <div className={styles.tagRow}>
            {tags.map((t, i) => (
              <span key={`${t}-${i}`} className={styles.tag}>#{t}</span>
            ))}
          </div>
        )}

        {/* Main description */}
        {description && <p className={styles.desc}>{description}</p>}


        {/* Experience comment */}
        {experience && (
          <div className={styles.expBox}>
            <QuoteIcon />
            <p>{experience}</p>
          </div>
        )}

         {/* Stats tiles */}
        {Array.isArray(stats) && stats.length > 0 && (
          <div className={styles.statsRow}>
            {stats.slice(0, 3).map((s, i) => (
              <div key={`${s.label}-${i}`} className={styles.statTile}>
                <div className={styles.statLabel}>{s.label}</div>
                <div className={styles.statValue}>{s.value}</div>
              </div>
            ))}
          </div>
        )}


        {/* CTAs */}
        <div className={styles.moreRow}>
          <button
            className={`${styles.cta} ${styles.secondary}`}
            aria-expanded={open}
            aria-controls={panelId}
            onClick={() => setOpen((v) => !v)}
          >
            <InfoIcon />
            {open ? "Hide info" : "More info"}
          </button>
          {link && (
            <a className={styles.cta} href={link} target="_blank" rel="noreferrer">
              Learn more
              <ArrowUpRight />
            </a>
          )}
        </div>

        {/* Expandable panel */}
        <div
          id={panelId}
          className={`${styles.morePanel} ${open ? styles.moreOpen : ""}`}
          role="region"
          aria-label="More information"
        >
          <div className={styles.metaGrid}>
            {role && (
              <div>
                <div className={styles.muted}>Role</div>
                <div>{role}</div>
              </div>
            )}
            {date && (
              <div>
                <div className={styles.muted}>Dates</div>
                <div>{formatDateRange(date, endDate)}</div>
              </div>
            )}
            {location && (
              <div>
                <div className={styles.muted}>Location</div>
                <div>{location}</div>
              </div>
            )}
          </div>
          {(details || description) && (
            <>
              <div className={styles.divider} />
              <p className={styles.moreText}>{details || description}</p>
            </>
          )}
        </div>
      </div>
    </article>
  );
}

function formatDate(d) {
  if (!d) return "";
  try {
    const dd = typeof d === "string" ? new Date(d) : d;
    return dd.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" });
  } catch {
    return String(d);
  }
}
function formatDateRange(start, end) {
  if (!start && !end) return "";
  if (!end) return formatDate(start);
  try {
    const s = typeof start === "string" ? new Date(start) : start;
    const e = typeof end === "string" ? new Date(end) : end;
    const sameMonth = s.getFullYear() === e.getFullYear() && s.getMonth() === e.getMonth();
    const startStr = s.toLocaleDateString(undefined, {
      month: "short",
      day: sameMonth ? "numeric" : "2-digit",
      year: sameMonth ? undefined : "numeric",
    });
    const endStr = e.toLocaleDateString(undefined, {
      month: sameMonth ? undefined : "short",
      day: "numeric",
      year: "numeric",
    });
    return `${startStr}–${endStr}`;
  } catch {
    return `${start}–${end}`;
  }
}

function ChevronLeft(props) { return (<svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true" {...props}><path d="M15.5 19l-7-7 7-7" fill="none" stroke="currentColor" strokeWidth="2"/></svg>); }
function ChevronRight(props) { return (<svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true" {...props}><path d="M8.5 5l7 7-7 7" fill="none" stroke="currentColor" strokeWidth="2"/></svg>); }
function ArrowUpRight(props) { return (<svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" {...props}><path d="M7 17L17 7M9 7h8v8" fill="none" stroke="currentColor" strokeWidth="2"/></svg>); }
function InfoIcon(props) { return (<svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" {...props}><circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2"/><path d="M12 10v6M12 7h.01" stroke="currentColor" strokeWidth="2"/></svg>); }
function QuoteIcon(props) { return (<svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" {...props}><path d="M7 11c0-3 2-5 5-5v3c-1 0-2 1-2 2v5H7v-5zm7 0c0-3 2-5 5-5v3c-1 0-2 1-2 2v5h-3v-5z" fill="currentColor"/></svg>); }
